import Emittery from "emittery";

import type { Config } from "../../config";
import { Disposition, Job, JobFunction } from "../../job";
import { ErrorCategory, Result, SuccessfulCategory } from "../../result";
import JobFailedError from "./job-failed-error";

export interface ItemResult {
	id: string;
	isSuccessful: boolean;
	category: string;
	duration: number;
	error?: Error;
}

export interface CategoryResult {
	isSuccessful: boolean;
	category: string;
	count: number;
}

export interface FunctionResult {
	name: JobFunction;
	duration: number;
	error?: Error;
}

export enum ExecutionState {
	NotStarted = "Not Started",
	Initializing = "Initializing",
	GettingItems = "Getting Items",
	Processing = "Processing",
	Finalizing = "Finalizing",
	Complete = "Complete"
}

type EventDataMap = {
	started: { startedAt: Date };
	stateChanged: { state: ExecutionState };
	itemsCounted: { count: number };
	itemCompleted: { result: ItemResult };
	functionCompleted: { result: FunctionResult };
	completed: { disposition: Disposition; completedAt: Date };
};

export enum Events {
	Started = "started",
	StateChanged = "stateChanged",
	ItemsCounted = "itemsCounted",
	ItemCompleted = "itemCompleted",
	FunctionCompleted = "functionCompleted",
	Completed = "completed"
}

export interface Run {
	state: ExecutionState;

	progress: RunProgress;

	results: ItemResult[];
	categories: { [id: string]: CategoryResult };

	startedAt: Date;
	completedAt: Date;
	cancelledAt: Date;
	failedAt: Date;
	failedIn: JobFunction;
	failedBecauseOf: Error;

	readonly isRunning: boolean;
	readonly isComplete: boolean;
	readonly isCancelled: boolean;
	readonly isFailed: boolean;
	readonly disposition: Disposition;
}

export interface RunProgress {
	total?: number;
	success: number;
	failed: number;
}

export default class Execution
	extends Emittery.Typed<EventDataMap, undefined>
	implements Run {
	constructor(job: Job<Config, unknown>, cfg: Config) {
		super();
		this.job = job;
		this.cfg = cfg;
		this.state = ExecutionState.NotStarted;
	}

	job: Job<Config, unknown>;
	cfg: Config;
	state: ExecutionState;

	items:
		| unknown[]
		| Promise<unknown[]>
		| AsyncIterator<unknown, void, unknown>
		| Iterator<Promise<unknown> | unknown, void, unknown>;

	totalItemCount?: number;
	successfulItemCount = 0;
	failedItemCount = 0;

	get progress(): RunProgress {
		return {
			total: this.totalItemCount,
			success: this.successfulItemCount,
			failed: this.failedItemCount
		};
	}

	results: ItemResult[];
	categories: { [id: string]: CategoryResult };

	startedAt: Date;
	completedAt: Date;
	cancelledAt: Date;
	failedAt: Date;
	failedIn: JobFunction;
	failedBecauseOf: Error;

	get isRunning(): boolean {
		return this.startedAt !== undefined && this.completedAt === undefined;
	}

	get isComplete(): boolean {
		return this.completedAt !== undefined;
	}

	get isCancelled(): boolean {
		return this.cancelledAt !== undefined;
	}

	get isFailed(): boolean {
		return this.failedAt !== undefined;
	}

	/* eslint-disable prettier/prettier */
	// The following is prettier than prettier/prettier.

	get disposition(): Disposition {
		return 	this.isFailed ? Disposition.Failed :
				this.isCancelled ? Disposition.Cancelled : 
				Disposition.Successful;
	}

	/* eslint-enable prettier/prettier */

	setState(state: ExecutionState): Promise<void> {
		this.state = state;
		return this.emit(Events.StateChanged, { state });
	}

	itemCompleted(result: ItemResult): Promise<void> {
		this.results.push(result);

		const catId = `${result.category};${result.isSuccessful.toString()}`;
		let cat = this.categories[catId];

		if (cat === undefined) {
			cat = {
				isSuccessful: result.isSuccessful,
				category: result.category,
				count: 1
			};
			this.categories[catId] = cat;
		} else {
			cat.count++;
		}

		if (result.isSuccessful) this.successfulItemCount++;
		else this.failedItemCount++;

		return this.emit(Events.ItemCompleted, { result });
	}

	functionCompleted(
		handlers: Promise<void>[],
		func: JobFunction,
		duration: number,
		error: Error,
		throwOnError?: boolean
	): void {
		if (throwOnError === undefined) throwOnError = true;

		const result = {
			name: func,
			duration,
			error
		} as FunctionResult;

		handlers.push(this.emit(Events.FunctionCompleted, { result }));

		if (error) {
			this.failedAt = new Date();
			this.failedIn = func;
			this.failedBecauseOf = error;
			if (throwOnError) throw new JobFailedError(this.failedAt, func, error);
		}
	}

	cancel(): void {
		if (!this.isComplete && !this.isCancelled && !this.isFailed) {
			this.cancelledAt = new Date();
		}
	}

	itemsIsArray(): boolean {
		return (this.items as unknown[]).length !== undefined;
	}

	async start(): Promise<void> {
		const handlers: Promise<void>[] = [];
		let error: Error;
		let start = 0;
		let end = 0;
		this.results = [];
		this.categories = {};

		try {
			// ==========================
			// 1. Initialize
			// ==========================
			this.startedAt = new Date();
			handlers.push(this.setState(ExecutionState.Initializing));
			handlers.push(this.emit(Events.Started, { startedAt: this.startedAt }));
			start = Date.now();
			try {
				if (this.job.init) await this.job.init(this.cfg);
			} catch (err) {
				error = err as Error;
			}
			end = Date.now();

			this.functionCompleted(handlers, JobFunction.Init, end - start, error);

			// ==========================
			// 2. Get Items
			// ==========================

			handlers.push(this.setState(ExecutionState.GettingItems));
			start = Date.now();
			try {
				this.items = await this.job.getItems(this.cfg);

				const itemsArray = this.items as unknown[];

				if (itemsArray.length !== undefined) {
					this.totalItemCount = itemsArray.length;
					handlers.push(
						this.emit(Events.ItemsCounted, { count: this.totalItemCount })
					);
					// items needs to be an iterator, so wave the magic wand:
					this.items = itemsArray[Symbol.iterator]();
				}
			} catch (err) {
				error = err as Error;
			}
			end = Date.now();

			this.functionCompleted(
				handlers,
				JobFunction.GetItems,
				end - start,
				error
			);

			// ==========================
			// 3. Process
			// ==========================

			handlers.push(this.setState(ExecutionState.Processing));
			const parallelTaskCount =
				this.cfg.execution.parallelTaskCount > 1
					? this.cfg.execution.parallelTaskCount
					: 1;

			const iterator = this.items as AsyncIterator<unknown, void, unknown>;
			const parallelTasks: Promise<void>[] = [];

			start = Date.now();
			try {
				for (let i = 0; i < parallelTaskCount; i++) {
					parallelTasks.push(this.processInParallel(iterator, handlers));
				}
			} finally {
				await Promise.allSettled(parallelTasks);
			}
			end = Date.now();

			if (!this.isFailed)
				this.functionCompleted(
					handlers,
					JobFunction.Process,
					end - start,
					undefined // eslint-disable-line unicorn/no-useless-undefined
				);
		} catch (err) {
			// This catch block intentionally eats the error when handleExceptions is true.
			// When HandleExceptions is true the inner try/catch blocks will handle all
			// errors thrown from a job and record them in the results. After
			// recording the results a JobFailedError is thrown to abort the remaining
			// work (except when items are retried or the job failure count has not
			// been exceeded). Since the exception has already been recorded there is nothing
			// do to here except prevent this error from propagating to the caller.
			// Finalize is guaranteed to be called, so it alone is in the finally block.
			if (!this.cfg.execution.handleExceptions) throw err;
		} finally {
			// ==========================
			// 4. Finalize
			// ==========================

			handlers.push(this.setState(ExecutionState.Finalizing));
			error = undefined;
			start = Date.now();
			try {
				if (this.job.finalize)
					await this.job.finalize(this.cfg, this.disposition);
			} catch (err) {
				error = err as Error;
			}
			end = Date.now();

			try {
				this.functionCompleted(
					handlers,
					JobFunction.Finalize,
					end - start,
					error,
					false
				);
			} finally {
				this.completedAt = new Date();
				handlers.push(this.setState(ExecutionState.Complete));
				handlers.push(
					this.emit(Events.Completed, {
						disposition: this.disposition,
						completedAt: this.completedAt
					})
				);

				await Promise.allSettled(handlers);
			}
		}
	}

	async processInParallel(
		iterator: AsyncIterator<unknown, void, unknown>,
		handlers: Promise<void>[]
	): Promise<void> {
		let cont = true;
		let iteration: IteratorResult<unknown, void>;

		while (cont && !this.isFailed && !this.isCancelled) {
			try {
				// eslint-disable-next-line no-await-in-loop
				iteration = await iterator.next();
				cont = cont && !iteration.done;
			} catch (err) {
				const error = err as Error;
				this.functionCompleted(handlers, JobFunction.GetItems, 0, error);
			}

			if (cont) await this.processItem(iteration.value, handlers); // eslint-disable-line no-await-in-loop
		}
	}

	async processItem(item: unknown, handlers: Promise<void>[]): Promise<void> {
		const maxAttempts =
			this.cfg.execution.itemFailureRetryCount > 1
				? this.cfg.execution.itemFailureRetryCount
				: 1;
		let cont = false;
		let attempt = 0;
		let start = 0;
		let end = 0;

		do {
			try {
				start = Date.now();
				cont = false;
				attempt++;

				const process = this.job.process(item, this.cfg, 1);
				const raw = await Promise.resolve(process); // eslint-disable-line no-await-in-loop
				end = Date.now();

				let result: Result;

				if (!raw) {
					result = { category: SuccessfulCategory, isSuccess: true };
				} else if (typeof raw === "string") {
					result = { category: raw, isSuccess: true };
				} else {
					result = raw;
				}

				handlers.push(
					this.itemCompleted({
						id: this.job.getItemId(item, this.cfg),
						isSuccessful: result.isSuccess,
						category: result.category,
						duration: end - start,
						error: result.error
					})
				);
			} catch (error) {
				end = Date.now();
				if (attempt === maxAttempts) {
					handlers.push(
						this.itemCompleted({
							id: this.job.getItemId(item, this.cfg),
							isSuccessful: false,
							category: ErrorCategory,
							duration: end - start,
							error: error as Error
						})
					);
					this.functionCompleted(
						handlers,
						JobFunction.Process,
						0,
						error as Error
					);
				} else {
					cont = true;
				}
			}
		} while (cont);
	}
}
